/**
 * Drafts: whatever you type in a form is kept in this browser as you type, so leaving the page,
 * switching tabs or closing the window loses nothing. Saving the form clears the draft; "Discard
 * draft" clears it by hand. Drafts are kept for 14 days and never leave this computer.
 */
import { h } from "./dom.ts";

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
const PREFIX = "ryoma.draft.";
const KEEP_DAYS = 14;

interface Stored { at: number; values: Record<string, string> }

function load(key: string): Stored | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const d = JSON.parse(raw) as Stored;
    if (!d.at || Date.now() - d.at > KEEP_DAYS * 864e5) { localStorage.removeItem(PREFIX + key); return null; }
    return d;
  } catch {
    return null;
  }
}

/** Whether a draft is waiting under this key (for a "Draft" marker on a closed panel). */
export const hasDraft = (key: string): boolean => load(key) !== null;

export interface Draft {
  /** True when a saved draft was put back into the fields */
  restored: boolean;
  /** "Draft saved 14:32 · Discard draft", to place under the form */
  status: HTMLElement;
  /** Call after the form is saved */
  clear(): void;
}

/**
 * Keeps the fields' values as a draft under `key`. Values typed differently from `blank` (the
 * starting values) count as a draft; when everything is back to blank the draft is dropped.
 */
export function keepDraft(key: string, fields: Record<string, Field>, onRestore?: () => void): Draft {
  const blank = Object.fromEntries(Object.entries(fields).map(([k, f]) => [k, f.value]));
  const when = h("span", { class: "draft-when" });
  const discard = h("button", { type: "button", class: "btn btn-quiet draft-discard" }, "Discard draft");
  const status = h("div", { class: "draft-status", "aria-live": "polite", hidden: true }, when, discard);
  let timer: ReturnType<typeof setTimeout> | undefined;

  const show = (at: number | null) => {
    status.hidden = at === null;
    when.textContent = at === null ? "" : `Draft saved ${new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  };
  const save = () => {
    const values = Object.fromEntries(Object.entries(fields).map(([k, f]) => [k, f.value]));
    const dirty = Object.keys(values).some((k) => values[k] !== blank[k]);
    try {
      if (dirty) localStorage.setItem(PREFIX + key, JSON.stringify({ at: Date.now(), values }));
      else localStorage.removeItem(PREFIX + key);
    } catch { /* storage blocked: the form still works, without drafts */ }
    show(dirty ? Date.now() : null);
  };
  const clear = () => {
    clearTimeout(timer);
    try { localStorage.removeItem(PREFIX + key); } catch { /* ignore */ }
    show(null);
  };

  const d = load(key);
  let restored = false;
  if (d) {
    for (const [k, v] of Object.entries(d.values)) {
      const f = fields[k];
      if (!f || f.value === v) continue;
      f.value = v;
      f.dispatchEvent(new Event("change", { bubbles: true }));
      restored = true;
    }
    if (restored) { show(d.at); onRestore?.(); }
  }
  for (const f of Object.values(fields)) {
    const later = () => { clearTimeout(timer); timer = setTimeout(save, 400); };
    f.addEventListener("input", later);
    f.addEventListener("change", later);
  }
  discard.addEventListener("click", () => {
    if (!confirm("Discard this draft?")) return;
    for (const [k, f] of Object.entries(fields)) { f.value = blank[k] ?? ""; f.dispatchEvent(new Event("change", { bubbles: true })); }
    clear();
  });
  window.addEventListener("pagehide", () => { if (timer) { clearTimeout(timer); save(); } });
  return { restored, status, clear };
}
